export const generateOpenEndedPrompt = (eventDetails, posts, stats) => {
  // console.log('⚙️ generateOpenEndedPrompt called with:', {
  //   hasEventDetails: !!eventDetails,
  //   eventDetailsKeys: eventDetails ? Object.keys(eventDetails) : [],
  //   hasPosts: !!posts,
  //   postsLength: posts?.length,
  //   hasStats: !!stats,
  //   statsKeys: stats ? Object.keys(stats) : [],
  //   timestamp: new Date().toISOString()
  // });

  if (!eventDetails) {
    console.error('❌ Missing eventDetails in generateOpenEndedPrompt');
    return null;
  }

  try {
    // Format event details and stats for the prompt
    const eventName = eventDetails?.name || 'N/A';
    const questionType = eventDetails?.question_type || 'default';
    const description = eventDetails?.description || 'N/A';
    
    // Access metrics data from the correct path
    const metricsData = stats?.metrics || {};
    const summaryData = stats?.summary || {};
    
    // console.log('📊 OpenEnded prompt - accessing metrics:', { 
    //   hasMetrics: !!metricsData, 
    //   metricsDataKeys: Object.keys(metricsData),
    //   hasSummary: !!summaryData,
    //   summaryDataKeys: Object.keys(summaryData),
    //   timestamp: new Date().toISOString() 
    // });
    
    // Extract total responses with comprehensive fallback paths
    let totalResponses = 0;
    
    // Try all possible paths for total responses in metrics
    if (metricsData && typeof metricsData === 'object') {
      totalResponses = metricsData.contributed || metricsData.choices || metricsData.responses || 
                      metricsData.ideas || metricsData.totalResponses || metricsData.total || 0;
    }
    
    // If summary has total responses, use that as a fallback
    if (totalResponses === 0 && summaryData && typeof summaryData === 'object') {
      totalResponses = summaryData.total_choices || summaryData.total_responses || 
                      summaryData.choices || summaryData.responses || summaryData.total || 0;
    }
    
    // Check top level stats as a last resort
    if (totalResponses === 0) {
      totalResponses = stats.total_choices || stats.total_responses || stats.choices || 
                      stats.responses || stats.ideas || stats.contributed || stats.total || 0;
    }
    
    // For open-ended questions, we can use post length as a last resort
    if (totalResponses === 0 && Array.isArray(posts)) {
      totalResponses = posts.length;
      // console.log('📊 OpenEnded - estimated totalResponses from posts array length:', totalResponses);
    }
    
    // console.log('🔎 OpenEnded - complete search for totalResponses:', {
    //   totalResponses,
    //   metricsData_paths: metricsData ? {
    //     contributed: metricsData.contributed,
    //     choices: metricsData.choices,
    //     responses: metricsData.responses,
    //     ideas: metricsData.ideas
    //   } : 'No metrics data',
    //   summaryData_paths: summaryData ? {
    //     total_choices: summaryData.total_choices,
    //     total_responses: summaryData.total_responses,
    //     choices: summaryData.choices
    //   } : 'No summary data',
    //   timestamp: new Date().toISOString()
    // });
    
    // Extract total users with enhanced fallbacks
    let totalUsers = 0;
    
    // Try all possible paths for totalUsers in metrics
    if (metricsData && typeof metricsData === 'object') {
      totalUsers = metricsData.total_user || metricsData.total_users || metricsData.viewed_user || 
                  metricsData.joined || metricsData.participants || metricsData.contributors || 0;
    }
    
    // Check summary data for user counts
    if (totalUsers === 0 && summaryData && typeof summaryData === 'object') {
      totalUsers = summaryData.total_user || summaryData.total_users || summaryData.users || 
                  summaryData.participants || summaryData.contributors || summaryData.joined || 0;
    }
    
    // Check top level stats as a last resort
    if (totalUsers === 0) {
      totalUsers = stats.total_user || stats.total_users || stats.users || 
                  stats.participants || stats.joined || stats.viewers || 0;
    }
    
    // If we have response data but no user count, estimate conservatively
    if (totalUsers === 0 && totalResponses > 0) {
      totalUsers = Math.max(Math.ceil(totalResponses * 1.2), totalResponses + 3);
      // console.log('⚠️ OpenEnded - estimated totalUsers:', totalUsers);
    }
    
    // Calculate participation rate with validation
    let participationRate = 0;
    
    // Try to get it directly from metrics or summary first
    if (metricsData?.participation_rate !== undefined && metricsData?.participation_rate !== null) {
      participationRate = metricsData.participation_rate;
    } else if (summaryData?.participation_rate !== undefined && summaryData?.participation_rate !== null) {
      participationRate = summaryData.participation_rate;
    } else if (stats?.participation_rate !== undefined && stats?.participation_rate !== null) {
      participationRate = stats.participation_rate;
    }
    // Otherwise calculate it if we have valid totalUsers
    else if (totalUsers > 0) {
      participationRate = Math.round((totalResponses / totalUsers) * 100);
    }
    
    // Validate participation rate is realistic (cannot be > 100%)
    if (participationRate > 100) {
      // console.log(`⚠️ OpenEnded - participation rate over 100%: ${participationRate}%, capping at 100%`);
      participationRate = 100;
    }
    
    // Extract average score with enhanced validation - note that open-ended questions typically
    // don't have an average score, but some implementations might use sentiment analysis or other scoring
    let averageScore = null;
    
    // Try to find the average score in all possible locations
    const possibleScorePaths = [
      summaryData?.simple_score,
      summaryData?.average_score,
      summaryData?.avg_score,
      summaryData?.score,
      summaryData?.sentiment_score,
      metricsData?.simple_score,
      metricsData?.average_score,
      metricsData?.avg_score,
      metricsData?.score,
      metricsData?.sentiment_score,
      stats?.simple_score,
      stats?.average_score,
      stats?.avg_score,
      stats?.score,
      stats?.sentiment_score
    ];
    
    // Find the first non-null, non-undefined score
    for (const path of possibleScorePaths) {
      if (path !== undefined && path !== null) {
        const parsedScore = parseFloat(path);
        if (!isNaN(parsedScore)) {
          averageScore = parsedScore;
          break;
        }
      }
    }
    
    // console.log('📊 OpenEnded - metrics extraction results:', {
    //   totalResponses,
    //   totalUsers,
    //   participationRate,
    //   averageScore,
    //   timestamp: new Date().toISOString()
    // });
    
    // Helper function to shuffle an array (Fisher-Yates algorithm)
    const shuffleArray = (array) => {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    // Format posts data with engagement metrics and smart sampling
    let formattedPosts = '';
    if (Array.isArray(posts) && posts.length > 0) {
      // Sort posts by engagement (likes + comments)
      const sortedPosts = [...posts].sort((a, b) => {
        const aEngagement = (a?.like_count || 0) + (a?.child_count || 0);
        const bEngagement = (b?.like_count || 0) + (b?.child_count || 0);
        return bEngagement - aEngagement; // Descending order
      });
      
      // Apply smart sampling to reduce prompt size
      const MAX_POSTS = 25; // Set a reasonable limit
      let processedPosts = sortedPosts;
      
      if (sortedPosts.length > MAX_POSTS) {
        // console.log(`📊 Applying smart sampling: ${sortedPosts.length} → ${MAX_POSTS} posts to reduce prompt size`);
        
        // Representative sampling - take high engagement and some random posts
        // 40% high engagement, 30% random, 30% from the end (lowest engagement) for balance
        const highEngagementCount = Math.floor(MAX_POSTS * 0.4);
        const randomCount = Math.floor(MAX_POSTS * 0.3);
        const lowEngagementCount = MAX_POSTS - highEngagementCount - randomCount;
        
        // Get high engagement posts
        const highEngagementPosts = sortedPosts.slice(0, highEngagementCount);
        
        // Get some random posts from the middle (excluding high and low engagement)
        const middlePosts = sortedPosts.slice(highEngagementCount, sortedPosts.length - lowEngagementCount);
        const randomPosts = shuffleArray(middlePosts).slice(0, randomCount);
        
        // Get some low engagement posts (to ensure representation of quiet voices)
        const lowEngagementPosts = sortedPosts.slice(sortedPosts.length - lowEngagementCount);
        
        // Combine and preserve overall engagement order
        processedPosts = [...highEngagementPosts, ...randomPosts, ...lowEngagementPosts];
      }
      
      // Helper function to truncate text content
      const truncateText = (text, maxLength = 300) => {
        if (!text || text.length <= maxLength) return text;
        // Try to truncate at a sentence boundary if possible
        const sentenceBreak = text.substring(0, maxLength).lastIndexOf('.');
        if (sentenceBreak > maxLength * 0.7) { // Only use sentence break if it's at least 70% of the way through
          return text.substring(0, sentenceBreak + 1) + ' [...]';
        }
        return text.substring(0, maxLength) + ' [...]';
      };
      
      // Format the sampled posts
      formattedPosts = processedPosts.map(post => {
        // Truncate long content to control prompt size
        const content = truncateText(post?.content || '', 300);
        const likeCount = post?.like_count || 0;
        const dislikeCount = post?.dislike_count || 0;
        const childCount = post?.child_count || 0;
        const engagementScore = likeCount + childCount;
        
        // Format comments if they exist, but limit number of comments shown
        let comments = '';
        if (Array.isArray(post?.children) && post.children.length > 0) {
          // Only include up to 3 comments per post to control size
          const MAX_COMMENTS = 3;
          comments = post.children
            .slice(0, MAX_COMMENTS)
            .map(child => {
              const childLikes = child?.like_count || 0;
              // Also truncate long comments
              return `- ${truncateText(child?.content || '', 150)} [Likes: ${childLikes}]`;
            }).join('\n');
          
          // Add note if we truncated comments
          if (post.children.length > MAX_COMMENTS) {
            comments += `\n- Plus ${post.children.length - MAX_COMMENTS} more comments`;
          }
        }
        
        // Add engagement metrics to the post
        return `"${content}" [Engagement: ${engagementScore}, Likes: ${likeCount}, Dislikes: ${dislikeCount}, Comments: ${childCount}]\n${comments}`;
      }).join('\n\n');
      
      // Add note about sampling if applicable
      if (posts.length > MAX_POSTS) {
        formattedPosts += `\n\n[Note: This is a representative sample of ${MAX_POSTS} responses from a total of ${posts.length}.]`;
      }
    } else {
      formattedPosts = 'No responses available';
    }
    
    // Cross-validate total responses if it's 0 but we have post data
    let validatedTotalResponses = totalResponses;
    if (validatedTotalResponses === 0 && Array.isArray(posts) && posts.length > 0) {
      // console.log('⚠️ Correcting zero totalResponses using posts.length');
      validatedTotalResponses = posts.length;
    }
    
    // Get sentiment data from metrics
    const sentimentPositive = metricsData.posts_sentiment_positive || 0;
    const sentimentNeutral = metricsData.posts_sentiment_neutral || 0;
    const sentimentNegative = metricsData.posts_sentiment_negative || 0;
    
    // Calculate sentiment percentages
    const totalSentiment = sentimentPositive + sentimentNeutral + sentimentNegative;
    const sentimentPositivePercent = totalSentiment > 0 ? Math.round((sentimentPositive / totalSentiment) * 100) : 0;
    const sentimentNeutralPercent = totalSentiment > 0 ? Math.round((sentimentNeutral / totalSentiment) * 100) : 0;
    const sentimentNegativePercent = totalSentiment > 0 ? Math.round((sentimentNegative / totalSentiment) * 100) : 0;

    // Get engagement metrics from metrics
    const likes = metricsData.comment_likes || metricsData.idea_likes || 0;
    const comments = metricsData.comments || 0;
    
    // Get engagement rate directly or calculate it simply
    let engagementRate = stats?.engagement_rate || 0;
    if (typeof engagementRate === 'number' && engagementRate <= 1) {
      engagementRate = Math.round(engagementRate * 100); // Convert from decimal to percentage
    }

    // console.log('📊 Extracted metrics with enhanced logic:', {
    //   likes,
    //   comments,
    //   engagementRate,
    //   postsAvailable: !!posts,
    //   postsLength: posts?.length,
    //   timestamp: new Date().toISOString()
    // });

    
    // Format the data section - removing statistics as they negatively impact LLM results
    const formattedData = `
Event Details:
- Name: ${eventName}
- Question Type: ${questionType}
- Description: ${description}

Responses (Sorted by Engagement):
${formattedPosts}
`;

// Create open ended question prompt template
// Calculate and log the prompt size reduction
const originalDataSize = formattedData.length;
const originalPromptLength = formattedData.length + 1500; // Approximate instruction length
const estimatedReduction = posts.length > 25 ? Math.round((1 - (25 / posts.length)) * 100) : 0;

// console.log('📊 Prompt size optimization:', {
//   originalDataSize,
//   optimizedDataSize: formattedData.length,
//   estimatedReduction: `${estimatedReduction}%`,
//   sampleRate: posts.length > 0 ? `${Math.min(25, posts.length)}/${posts.length} posts (${Math.round((Math.min(25, posts.length) / posts.length) * 100)}%)` : 'N/A',
//   timestamp: new Date().toISOString()
// });

const promptTemplate = `# Open-Ended Response Analysis Task

You are an expert data analyst specializing in qualitative response analysis. You have been tasked with analyzing open-ended responses from a survey or feedback session. Analyze the following data and provide a structured report in markdown format:

## Event Analysis Data
${formattedData}

## Analysis Instructions
1. Identify patterns and themes across responses beyond basic word frequency
2. Look for emotional undertones and implicit meanings, not just explicit statements
3. Identify unique insights or outlier opinions that may be valuable despite being uncommon
4. Look for actionable feedback and constructive suggestions embedded in responses
5. If engagement metrics (likes, comments) are present and nonzero, analyze which responses or themes received more engagement. If all engagement is zero, note this and focus analysis on content diversity and nuance instead.
6. Comment on any limitations in the data that affect the depth or breadth of analysis.
7. Provide suggestions for improving the survey question or format to generate richer, more actionable, or more diverse responses.

## Report Format
Format your response with these exact section headings using single # for each section. Ensure each section has substantive content:

# Executive Summary
[1-2 paragraph summary focusing on the key narrative emerging from responses]

# Analysis
- [Bullet points with insights about response patterns and their significance]
- [Include analysis of both explicit statements and implicit meanings]
- [Note any emotional undertones or sentiment patterns]
- [Highlight any subtle differences or variations in how respondents expressed themselves]

# Key Themes
- [List 3-5 major themes with examples from the responses]
- [Focus on underlying concerns, values, and priorities]
- [Include both positive and constructive feedback themes]
- [If engagement is present and nonzero, highlight which themes generated the most engagement]

# Recommendations
- [Provide 3-5 actionable next steps based on the feedback]
- [Include specific suggestions for addressing concerns]
- [Recommend strategies for leveraging positive feedback]
- [Suggest follow-up questions to explore identified themes further]
- [Prioritize recommendations based on engagement levels with related themes]

# Limitations
- [List any limitations in the data that affect the depth or breadth of analysis. Suggest what additional data would be helpful.]

# Survey Design Feedback
- [Provide suggestions for improving the survey question or format to generate richer, more actionable, or more diverse responses.]
`;

// console.log('✅ Open ended prompt generated:', {
//   promptLength: promptTemplate.length,
//   timestamp: new Date().toISOString()
// });

return promptTemplate;
} catch (error) {
  console.error('❌ Error in generateOpenEndedPrompt:', {
    error: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString()
  });
  return null;
}
}